import { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import GhostButton from '../../shared/button/GhostButton';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  fetchEntities,
  fetchMetricAliases,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import {
  EntityLookupItem,
  MetricAliasItem,
  MetricAliasListResult,
  MetricAliasStatus,
  ProblemDetail
} from '../../../types/syntiro_honey';
import RecordDetailModal from '../review/RecordDetailModal';
import { ALIAS_STATUS_DISPLAY } from './alias_status';
import ResolveRuleDialog from './ResolveRuleDialog';
import RetireRuleDialog from './RetireRuleDialog';

/** Debounce for the label search — matches the review filter bar's feel. */
const SEARCH_DEBOUNCE_MS = 400;

/** The status dropdown's options: the three lifecycle states plus "all". */
const STATUS_OPTIONS: Array<{ value: MetricAliasStatus | ''; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CONFLICTED', label: 'Contested' },
  { value: 'RETIRED', label: 'Retired' },
  { value: '', label: 'All statuses' }
];

/**
 * The learned-rules management surface (#43 / ADR-0003): what the system has
 * learned for one client — every "label → metric" rule the reviewers taught —
 * with the two editor actions the loop cannot perform on its own: retiring a
 * wrong rule and resolving a contested label. Strictly one client at a time:
 * rules are company-scoped and never cross clients.
 * @returns {JSX.Element}
 */
export default function LearnedRulesPanel() {
  // Client picker: same source + degradation as the upload step's picker —
  // an unreachable catalogue reads as a disabled field, never as an empty page.
  const [companies, setCompanies] = useState<Array<EntityLookupItem>>([]);
  const [companiesUnavailable, setCompaniesUnavailable] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<string>('');

  const [status, setStatus] = useState<MetricAliasStatus | ''>('ACTIVE');
  const [searchDraft, setSearchDraft] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [result, setResult] = useState<MetricAliasListResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const [retireTarget, setRetireTarget] = useState<MetricAliasItem | null>(null);
  const [resolveTarget, setResolveTarget] = useState<MetricAliasItem | null>(null);
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);

  const { company } = useContext(CompanyContext);
  const { group } = useContext(GroupContext);
  const login_client_code = company?.client_code ?? group?.client_code ?? null;

  useEffect(() => {
    let cancelled = false;
    // Always fetched unscoped: Honey's companyId for the logged-in client is only
    // recoverable AFTER this call, by matching client_code against the clientCode
    // on a returned COMPANY row (a companyId-scoped request 422s — esg-platform's
    // own company id is not a Honey UUID).
    fetchEntities()
      .then((entities) => {
        if (cancelled) {
          return;
        }
        const allCompanies = entities.filter((entity) => entity.kind === 'COMPANY');
        if (login_client_code === null) {
          // EP staff keep the full cross-client list (existing, tested behaviour).
          setCompanies(allCompanies);
          return;
        }
        const matchedCompany = allCompanies.find(
          (candidate) => candidate.clientCode === login_client_code
        );
        // `companies` is a COMPANY-kind-only list (the picker/assigned_company
        // source), so the companyId filter is applied within that set — a client
        // user has exactly one matching row, never a division/subdivision/site.
        setCompanies(
          matchedCompany === undefined
            ? []
            : allCompanies.filter((candidate) => candidate.companyId === matchedCompany.companyId)
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCompaniesUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [login_client_code]);

  // Client-from-login, the same rule the upload step applies (OQ24): when the
  // company (or its group) carries the catalogue client code, the client is
  // ASSIGNED and the picker locks. Rules are a client's own learned vocabulary, so
  // a client user must not be able to read another client's — the dropdown listed
  // every client to everyone (Maxine 2026-08-14 ⑫). EP staff, who carry no client
  // code, keep the full list.
  const assigned_company =
    login_client_code !== null
      ? (companies.find((candidate) => candidate.clientCode === login_client_code) ?? null)
      : null;
  useEffect(() => {
    if (assigned_company !== null && companyId !== assigned_company.companyId) {
      setCompanyId(assigned_company.companyId);
      setPage(0);
    }
  }, [assigned_company, companyId]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchDraft.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  useEffect(() => {
    setResult(null);
    if (companyId === '') {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchMetricAliases({
      companyId,
      status: status === '' ? undefined : status,
      search: search === '' ? undefined : search,
      limit: pageSize,
      offset: page * pageSize
    })
      .then((page_result) => {
        if (!cancelled) {
          setResult(page_result);
          setProblem(null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setProblem(toProblemDetail(error));
          setResult(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, status, search, page, pageSize, refreshKey]);

  const refresh = (): void => setRefreshKey((key) => key + 1);

  return (
    <Box sx={{ py: 3 }}>
      <Stack spacing={2}>
        <CaptionText>
          Every metric a reviewer confirms teaches a rule for that client: the same label then maps
          automatically on future uploads. Rules never cross clients. Retire a rule that is wrong;
          resolve a contested label to switch its automatic mapping back on.
        </CaptionText>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          {assigned_company !== null ? (
            // Locked, not hidden: the reviewer must see WHOSE rules they are reading.
            <TextField
              size="small"
              label="Client"
              sx={{ minWidth: 220 }}
              value={assigned_company.name}
              helperText="Taken from the company you are logged in as."
              disabled
            />
          ) : (
            <TextField
              select
              size="small"
              label="Client"
              sx={{ minWidth: 220 }}
              value={companyId}
              onChange={(event) => {
                setCompanyId(event.target.value);
                setPage(0);
              }}
              disabled={companiesUnavailable}
              helperText={companiesUnavailable ? 'Client list unavailable' : undefined}
            >
              <MenuItem value="">Pick a client…</MenuItem>
              {companies.map((option) => (
                <MenuItem key={option.companyId} value={option.companyId}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            size="small"
            label="Status"
            sx={{ minWidth: 160 }}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as MetricAliasStatus | '');
              setPage(0);
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.label} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search label"
            sx={{ minWidth: 220 }}
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </Stack>
        {problem !== null && (
          <Alert severity="error">
            {problem.detail ?? problem.title ?? 'Could not load the learned rules.'}
          </Alert>
        )}
        {companyId === '' ? (
          <BodyText>Pick a client to see what the system has learned for them.</BodyText>
        ) : loading && result === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : result !== null && result.items.length === 0 ? (
          <BodyText>
            No learned rules{status !== '' || search !== '' ? ' match these filters' : ' yet'} —
            confirming metrics during review teaches them.
          </BodyText>
        ) : result !== null ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client label</TableCell>
                  <TableCell>Maps to</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Taught by</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.items.map((rule) => {
                  const display = ALIAS_STATUS_DISPLAY[rule.status];
                  return (
                    <TableRow key={rule.id} hover>
                      <TableCell>
                        <BodyText>{rule.rawLabel}</BodyText>
                        {rule.unitText !== null && <CaptionText>{rule.unitText}</CaptionText>}
                      </TableCell>
                      <TableCell>
                        <BodyText>{rule.metricName}</BodyText>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={display.label}
                          size="small"
                          color={display.color}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{rule.createdByName ?? rule.createdBy}</TableCell>
                      <TableCell>
                        <BodyText>{new Date(rule.createdAt).toLocaleString()}</BodyText>
                        {rule.closedReason !== null && (
                          <CaptionText>{rule.closedReason}</CaptionText>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          {rule.sourceRecordId !== null && (
                            <Tooltip title="Open the record whose confirmation taught this rule">
                              <IconButton
                                size="small"
                                onClick={() => setOpenRecordId(rule.sourceRecordId)}
                                aria-label="Open source record"
                              >
                                <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {rule.status === 'ACTIVE' && (
                            <GhostButton
                              dark
                              size="small"
                              disabled={loading}
                              onClick={() => setRetireTarget(rule)}
                            >
                              Retire
                            </GhostButton>
                          )}
                          {rule.status === 'CONFLICTED' && (
                            <GhostButton
                              dark
                              size="small"
                              disabled={loading}
                              onClick={() => setResolveTarget(rule)}
                            >
                              Resolve…
                            </GhostButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={result.total}
              page={page}
              onPageChange={(_event, next) => setPage(next)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        ) : null}
      </Stack>
      {retireTarget !== null && (
        <RetireRuleDialog
          rule={retireTarget}
          onClose={() => setRetireTarget(null)}
          onRetired={() => {
            setRetireTarget(null);
            refresh();
          }}
        />
      )}
      {resolveTarget !== null && (
        <ResolveRuleDialog
          rule={resolveTarget}
          onClose={() => setResolveTarget(null)}
          onResolved={() => {
            setResolveTarget(null);
            refresh();
          }}
        />
      )}
      {/* Read-only: this surface manages RULES; record corrections stay in Review. */}
      <RecordDetailModal
        readOnly
        recordId={openRecordId}
        onClose={() => setOpenRecordId(null)}
        onRecordMutated={() => undefined}
        recordIds={openRecordId === null ? [] : [openRecordId]}
        onNavigate={() => undefined}
      />
    </Box>
  );
}
